import { forwardRef } from 'react';
import Card, { type CardProps } from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardHeader from '@mui/material/CardHeader';
import CardActions from '@mui/material/CardActions';
import Box from '@mui/material/Box';
import { keyframes } from '@mui/material/styles';

const cardEnter = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

export interface AppCardProps extends Omit<CardProps, 'title'> {
  /**
   * Card header title
   */
  title?: React.ReactNode;
  /**
   * Card header subtitle
   */
  subtitle?: React.ReactNode;
  /**
   * Action component for the header (e.g., icon button)
   */
  headerAction?: React.ReactNode;
  /**
   * Card footer actions
   */
  actions?: React.ReactNode;
  /**
   * Whether to animate the card on mount
   */
  animate?: boolean;
  /**
   * Animation delay in ms
   */
  animationDelay?: number;
  /**
   * Whether this is a highlighted/featured card
   */
  highlighted?: boolean;
  /**
   * Disable hover effects
   */
  disableHover?: boolean;
}

/**
 * AppCard - Standard card component
 *
 * Wraps MUI Card with our design system patterns.
 * Use for content containers, list items, and feature cards.
 */
export const AppCard = forwardRef<HTMLDivElement, AppCardProps>(
  (
    {
      title,
      subtitle,
      headerAction,
      actions,
      animate = false,
      animationDelay = 0,
      highlighted = false,
      disableHover = false,
      children,
      sx,
      ...props
    },
    ref
  ) => {
    return (
      <Card
        ref={ref}
        sx={{
          ...(animate && {
            animation: `${cardEnter} 400ms cubic-bezier(0.4, 0, 0.2, 1) ${animationDelay}ms backwards`,
          }),
          ...(highlighted && {
            borderColor: 'primary.main',
            borderWidth: 2,
            boxShadow: (theme) =>
              `0 4px 16px ${theme.palette.mode === 'light' ? 'rgba(25, 118, 210, 0.15)' : 'rgba(144, 202, 249, 0.15)'}`,
          }),
          ...(disableHover && {
            '&:hover': {
              borderColor: 'divider',
              boxShadow: 'none',
            },
          }),
          ...sx,
        }}
        {...props}
      >
        {(title || subtitle || headerAction) && (
          <CardHeader
            title={title}
            subheader={subtitle}
            action={headerAction}
            titleTypographyProps={{ variant: 'h6' }}
            subheaderTypographyProps={{ variant: 'body2', color: 'text.secondary' }}
            sx={{ pb: children ? 0 : undefined }}
          />
        )}
        {children && (
          <CardContent sx={{ pt: title ? 1 : undefined }}>
            {children}
          </CardContent>
        )}
        {actions && (
          <CardActions sx={{ px: 2, pb: 2, pt: 0, justifyContent: 'flex-end' }}>
            {actions}
          </CardActions>
        )}
      </Card>
    );
  }
);

AppCard.displayName = 'AppCard';

// Re-export MUI Card parts for flexibility
export { CardContent, CardHeader, CardActions, Box as CardBox };
